import { AsyncButton, Button, ColoredAlert, showDialog } from '@bpinternal/ui-kit-next'
import { CircleAlert } from 'lucide-react'
import { Boundary } from '~/components'
import { useUsage } from '~/hooks'
import { ADDONS_QUOTA_TYPES, useAddons } from '../hooks/useAddons'
import { usePlanProducts } from '../hooks/usePlanProducts'
import { usePurchasePlan } from '../hooks/usePurchasePlan'
import { getLegacyNameFromPlanId, getOrderedPlansInfo } from '../utils/plan'
import { PlanSummary } from './PlanSummary'

type DowngradePlanDialogProps = {
  workspaceId: string
  desiredPlanId: string
  isYearly: boolean
  close: () => void
}

export const showDowngradePlanDialog = ({
  workspaceId,
  desiredPlanId,
  isYearly,
}: Omit<DowngradePlanDialogProps, 'close'>) => {
  return showDialog(
    ({ close }: { close: () => void }) => {
      return (
        <Boundary>
          <DowngradePlanDialog
            workspaceId={workspaceId}
            desiredPlanId={desiredPlanId}
            isYearly={isYearly}
            close={close}
          />
        </Boundary>
      )
    },
    {
      size: 'max-w-4xl',
      title: 'Are you sure you want to downgrade?',
    }
  )
}

const DowngradePlanDialog = ({ workspaceId, desiredPlanId, isYearly, close }: DowngradePlanDialogProps) => {
  const { addons: currentAddons } = useAddons(workspaceId)
  const { mutate: purchasePlan, isPending } = usePurchasePlan(workspaceId, close)
  const { planProducts, currentPlan } = usePlanProducts(workspaceId)

  const desiredPlanProduct = planProducts.find(
    (product) => product.id === desiredPlanId && product.cycle === (isYearly ? 'yearly' : 'monthly')
  )
  const desiredPlanLegacyName = getLegacyNameFromPlanId(desiredPlanId)

  const availablePlans = getOrderedPlansInfo()
  const currentPlanInfo = availablePlans[currentPlan.legacyName]
  const desiredPlanInfo = availablePlans[desiredPlanLegacyName]

  const maxUsageAfterDowngrade = ADDONS_QUOTA_TYPES.reduce(
    (acc, quotaType) => {
      const addonQuantity = currentAddons.find((addon) => addon.quotaName === quotaType)?.nextPaymentQuantity ?? 0
      const planValue = desiredPlanInfo.addons?.find((addon) => addon.quotaType === quotaType)?.value ?? 0
      acc[quotaType] = addonQuantity + planValue
      return acc
    },
    {
      invocation_calls: 0,
      table_row_count: 0,
      bot_count: 0,
      always_alive: 0,
      workspace_member_count: 0,
      knowledgebase_vector_storage: 0,
      storage_count: 0,
    }
  )

  const usage = useUsage({
    workspaceId,
    quotas: [...ADDONS_QUOTA_TYPES],
  })

  const willBeOffending = (Object.keys(maxUsageAfterDowngrade) as Array<keyof typeof maxUsageAfterDowngrade>).some(
    (quotaType) => {
      return usage[quotaType].value > maxUsageAfterDowngrade[quotaType]
    }
  )

  return (
    <div className="flex flex-col gap-6">
      <div>
        Your plan features remain available <span className="font-semibold">until this billing cycle ends. </span>After
        that, you'll lose access to:
      </div>
      <PlanSummary plan={currentPlanInfo.legacyName} upgrade={false} />
      {willBeOffending && (
        <ColoredAlert color="amber">
          <CircleAlert />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">Warning</span>
            <p className="text-sm leading-relaxed">
              Your current usage exceeds the limits of the plan you're downgrading to. If you proceed, your bot may stop
              functioning until you reduce usage or upgrade your plan.
            </p>
          </div>
        </ColoredAlert>
      )}
      <div className="flex justify-end">
        <div className="flex gap-2">
          <Button onClick={close} variant="secondary">{`Stay on ${currentPlanInfo.displayName}`}</Button>
          <AsyncButton
            variant={'destructive'}
            disabled={isPending}
            loading={isPending}
            onClick={() => {
              if (desiredPlanProduct) {
                purchasePlan({ desiredPlan: desiredPlanProduct, isYearly })
              }
            }}
          >
            Confirm
          </AsyncButton>
        </div>
      </div>
    </div>
  )
}
